import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { register } from "../../services/authService";
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  LockClosedIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

const TaxFactRotator = () => {
  const facts = [
    "Direktorat Jenderal Pajak (DJP) adalah instansi Kemenkeu yang bertugas merumuskan dan melaksanakan kebijakan di bidang pajak.",
    "Surat Pemberitahuan (SPT) adalah dokumen yang digunakan Wajib Pajak untuk melaporkan penghitungan dan pembayaran pajak.",
    "Pajak Penghasilan (PPh) 21 dipotong atas penghasilan berupa gaji, upah, honorarium, tunjangan, dan pembayaran lain terkait pekerjaan.",
    "Coretax Administration System (CTAS) adalah sistem administrasi perpajakan terintegrasi terbaru dari DJP.",
    "E-Bupot Unifikasi adalah sistem administrasi yang digunakan untuk membuat Bukti Pemotongan dan pelaporan SPT Masa PPh secara terpadu.",
    "Faktur Pajak adalah bukti pungutan pajak yang dibuat oleh Pengusaha Kena Pajak (PKP) yang melakukan penyerahan BKP atau JKP.",
  ];
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % facts.length);
        setFade(true);
      }, 500);
    }, 6000);
    return () => clearInterval(interval);
  }, [facts.length]);

  return (
    <p
      className={`text-xs text-text-sec italic mb-2 transition-opacity duration-500 ${
        fade ? "opacity-100" : "opacity-0"
      }`}
    >
      "{facts[index]}"
    </p>
  );
};

export default function SignUpPage() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || success) return;

    setLoading(true);
    setError("");

    try {
      await register(formData);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error(err);
      const serverMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Pendaftaran gagal. Silakan coba lagi.";
      setError(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`@keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}
      </style>

      <div className="font-sans min-h-screen bg-brand text-text-pri flex items-center justify-center relative overflow-hidden p-4">
        {/* --- BACKGROUND AMBIANCE --- */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[20%] w-[500px] h-[500px] bg-brand/20 rounded-full mix-blend-screen filter blur-[120px] animate-pulse duration-[8s]"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-brand/30 rounded-full mix-blend-screen filter blur-[120px] animate-pulse delay-1000 duration-[10s]"></div>
        </div>

        {/* --- FLOATING CARD --- */}
        <div className="z-10 w-full max-w-5xl bg-card-bg backdrop-blur-xl border border-ledger-border rounded-3xl shadow-2xl flex flex-col md:flex-row overflow-hidden relative animate-fadeIn">
          {/* ORNAMENT */}
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-brand/30 rounded-full blur-3xl pointer-events-none"></div>

          {/* 1. LEFT SIDE */}
          <div className="hidden md:flex w-5/12 bg-paper p-10 flex-col justify-between border-r border-ledger-border relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-low border border-brand/30 text-brand text-[10px] font-bold uppercase tracking-widest mb-6">
                Enterprise Ready
              </div>
              <h2 className="text-3xl font-extrabold leading-tight mb-4 text-text-pri">
                Join the Future of <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand to-blue-500">
                  TACIA.
                </span>
              </h2>
              <p className="text-text-sec text-sm leading-relaxed">
                Tax, Administration &amp; Compliance Integrated Assistance
              </p>
            </div>

            <div className="relative z-10">
              <div className="p-4 rounded-xl bg-card-bg border border-ledger-border backdrop-blur-sm">
                <TaxFactRotator />
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-brand flex items-center justify-center text-[10px] font-bold text-white">
                    TK
                  </div>
                  <span className="text-[10px] font-bold text-text-sec">
                    Tax Knowledge
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 2. RIGHT SIDE */}
          <div className="w-full md:w-7/12 p-8 md:p-12 bg-card-bg text-text-pri relative">
            <div className="flex justify-between items-start mb-8">
              <div>
                <h3 className="text-2xl font-bold text-text-pri">Create Account</h3>
                <p className="text-text-sec text-sm mt-1">
                  Mulai akses platform kepatuhan dan konsultasi perpajakan.
                </p>
              </div>
              <img
                src="/tacia-icon.png"
                alt="Tacia Logo"
                className="h-10 w-10 object-contain drop-shadow-md"
              />
            </div>

            {success ? (
              <div className="py-12 text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircleIcon className="h-16 w-16 text-emerald-600 animate-bounce" />
                </div>
                <h4 className="text-xl font-bold text-text-pri">
                  Pendaftaran Berhasil
                </h4>
                <p className="text-text-sec text-sm max-w-sm mx-auto">
                  Akun Anda telah berhasil didaftarkan. Mengalihkan Anda ke halaman login secara otomatis...
                </p>
                <div className="pt-4">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition shadow-md"
                  >
                    Masuk Sekarang
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200 flex items-center gap-2 animate-pulse">
                    <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Full Name */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">
                    Nama Lengkap
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      className="w-full pl-10 pr-4 py-2.5 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="Nama Lengkap"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Company Name */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">
                    Perusahaan / Organisasi
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOfficeIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type="text"
                      name="company"
                      className="w-full pl-10 pr-4 py-2.5 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="Nama Perusahaan (Opsional)"
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Work Email */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">
                    Work Email
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      className="w-full pl-10 pr-4 py-2.5 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="name@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">
                    Password
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="w-full pl-10 pr-10 py-2.5 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="Minimal 8 karakter"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-sec hover:text-brand transition focus:outline-none"
                      title={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="terms-check"
                    className="mt-1 rounded border-ledger-border text-brand focus:ring-brand"
                    required
                  />
                  <label htmlFor="terms-check" className="text-xs text-text-sec cursor-pointer select-none">
                    Saya menyetujui Ketentuan Layanan dan Kebijakan Privasi PT Wibi Digital Technology.
                  </label>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-brand text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition shadow-xl shadow-brand/20 flex items-center justify-center gap-2 group disabled:opacity-70 mt-2"
                >
                  {loading ? "Mendaftarkan Akun..." : "Daftar Sekarang"}
                  {!loading && (
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition" />
                  )}
                </button>
              </form>
            )}

            <div className="mt-8 text-center">
              <p className="text-sm text-text-sec">
                Sudah memiliki akun?{" "}
                <Link to="/login" className="text-brand font-bold hover:underline">
                  Masuk
                </Link>
              </p>
            </div>

            <div className="mt-6 text-center">
              <span className="text-[10px] text-text-sec font-medium">
                Secured by Wibi Digital Technology
              </span>
            </div>
          </div>
        </div>

        <div className="absolute bottom-3 text-center w-full text-[10px] text-white/70 font-medium pointer-events-none">
          &copy; 2026 PT Wibi Digital Technology. All Rights Reserved.
        </div>
      </div>
    </>
  );
}

function ExclamationCircleIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}
